import { Controller, useFormContext } from 'react-hook-form'

import { RichTextEditor } from '@/components/rich-text'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'

// Ô nhập dùng chung của các màn Bán lẻ, gắn react-hook-form qua ui/field:
// nhãn + ô + gợi ý + lỗi (lỗi client lẫn lỗi server setError) ngay dưới ô.
// `name` cũng là id của ô để <Label htmlFor> của Field trỏ đúng.

export function TextField({
    name,
    label,
    hint,
    rows,
}: {
    name: string
    label: string
    hint?: string
    /** Có = ô nhiều dòng (Textarea). */
    rows?: number
}) {
    const { register, getFieldState, formState } = useFormContext()
    const invalid = !!getFieldState(name, formState).error
    return (
        <Field name={name} label={label}>
            {rows ? (
                <Textarea
                    id={name}
                    rows={rows}
                    aria-invalid={invalid}
                    {...register(name)}
                />
            ) : (
                <Input id={name} aria-invalid={invalid} {...register(name)} />
            )}
            {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </Field>
    )
}

/** Ô nội dung soạn bằng trình soạn dùng chung (HTML theo SPEC; trống = ''). */
export function RichTextField({
    name,
    label,
    hint,
    readOnly,
}: {
    name: string
    label: string
    hint?: string
    readOnly?: boolean
}) {
    const { control } = useFormContext()
    return (
        <Field name={name} label={label}>
            <Controller
                control={control}
                name={name}
                render={({ field }) => (
                    <RichTextEditor
                        id={name}
                        aria-label={label}
                        ownerHint={label}
                        value={
                            typeof field.value === 'string' ? field.value : ''
                        }
                        onChange={field.onChange}
                        readOnly={readOnly}
                    />
                )}
            />
            {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </Field>
    )
}

export function SwitchField({ name, label }: { name: string; label: string }) {
    const { control } = useFormContext()
    return (
        <Controller
            control={control}
            name={name}
            render={({ field }) => (
                <div className="flex items-center gap-2">
                    <Switch
                        id={name}
                        checked={!!field.value}
                        onCheckedChange={field.onChange}
                    />
                    <Label htmlFor={name}>{label}</Label>
                </div>
            )}
        />
    )
}

/** Lỗi không gắn ô nào (lỗi chung của form, lỗi upload). */
export function ErrorText({ text }: { text?: string | null }) {
    return text ? (
        <p className="text-xs text-destructive" role="alert">
            {text}
        </p>
    ) : null
}
